import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  Alert,
  InputAdornment,
  MenuItem,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';

interface StepPhoneNumberProps {
  formData: any;
  onComplete: (data: any) => void;
  onNext: () => void;
  onBack?: () => void;
  isActive: boolean;
}

const phoneSchema = yup.object({
  business_phone_number: yup
    .string()
    .matches(/^\+?1?\d{9,15}$/, 'Format de numéro invalide. Ex: +22507123456')
    .required('Le numéro de téléphone est requis'),
  country_code: yup.string().required('Le code pays est requis'),
});

// Codes pays les plus courants pour l'Afrique de l'Ouest
const countryCodes = [
  { code: '+225', country: 'Côte d\'Ivoire', flag: '🇨🇮', keepLeadingZero: true },
  { code: '+221', country: 'Sénégal', flag: '🇸🇳' },
  { code: '+223', country: 'Mali', flag: '🇲🇱' },
  { code: '+226', country: 'Burkina Faso', flag: '🇧🇫' },
  { code: '+229', country: 'Bénin', flag: '🇧🇯' },
  { code: '+227', country: 'Niger', flag: '🇳🇪' },
  { code: '+228', country: 'Togo', flag: '🇹🇬' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
];

export const formatBusinessPhoneNumber = (phoneNumber: string, countryCode: string): string => {
  let normalizedNumber = phoneNumber.trim().replace(/[\s().-]/g, '');

  if (normalizedNumber.startsWith('00')) {
    normalizedNumber = `+${normalizedNumber.substring(2)}`;
  }

  if (normalizedNumber.startsWith('+')) {
    const digits = normalizedNumber.substring(1);
    if (digits.startsWith('225')) {
      const nationalNumber = digits.substring(3);
      if (nationalNumber.length === 9 && /[157]/.test(nationalNumber.charAt(0))) {
        return `+2250${nationalNumber}`;
      }
    }
    return normalizedNumber;
  }

  const country = countryCodes.find(({ code }) => code === countryCode);
  if (!country?.keepLeadingZero && normalizedNumber.startsWith('0')) {
    normalizedNumber = normalizedNumber.substring(1);
  }

  return `${countryCode}${normalizedNumber}`;
};

export const StepPhoneNumber: React.FC<StepPhoneNumberProps> = ({
  formData,
  onComplete,
  onNext,
  onBack,
  isActive,
}) => {
  const [isValidating, setIsValidating] = useState(false);

  const formik = useFormik({
    initialValues: {
      business_phone_number: formatBusinessPhoneNumber(
        formData?.business_phone_number || '',
        formData?.country_code || '+225',
      ),
      country_code: formData?.country_code || '+225',
    },
    // The configuration is loaded asynchronously when editing an existing tenant.
    enableReinitialize: true,
    validationSchema: phoneSchema,
    onSubmit: async (values) => {
      setIsValidating(true);
      
      try {
        const formattedNumber = formatBusinessPhoneNumber(
          values.business_phone_number,
          values.country_code,
        );

        // Valider le format final
        const phoneRegex = /^\+[1-9]\d{8,14}$/;
        if (!phoneRegex.test(formattedNumber)) {
          throw new Error('Format de numéro invalide après formatage');
        }

        const stepData = {
          business_phone_number: formattedNumber,
          country_code: values.country_code,
        };

        onComplete(stepData);
      } catch (error: any) {
        formik.setFieldError('business_phone_number', error.message);
      } finally {
        setIsValidating(false);
      }
    },
  });

  // Auto-complétion du code pays basé sur le numéro
  useEffect(() => {
    const phoneNumber = formik.values.business_phone_number;
    if (phoneNumber.startsWith('+')) {
      const foundCountry = countryCodes.find(c => phoneNumber.startsWith(c.code));
      if (foundCountry && foundCountry.code !== formik.values.country_code) {
        formik.setFieldValue('country_code', foundCountry.code);
      }
    }
  }, [formik.values.business_phone_number]);

  if (!isActive) return null;

  return (
    <Box sx={{ py: 2 }}>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* Informations importantes */}
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Vous devez posséder un <strong>numéro WhatsApp Business</strong> vérifié pour continuer.
                Ce numéro sera utilisé pour recevoir et envoyer des messages FAQ.
              </Typography>
            </Alert>
          </Grid>

          {/* Formulaire numéro */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              {/* Sélecteur code pays */}
              <TextField
                select
                label="Pays"
                name="country_code"
                value={formik.values.country_code}
                onChange={formik.handleChange}
                sx={{ minWidth: 150 }}
                size="small"
              >
                {countryCodes.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.flag} {country.code}
                  </MenuItem>
                ))}
              </TextField>

              {/* Champ numéro */}
              <TextField
                fullWidth
                label="Numéro WhatsApp Business"
                name="business_phone_number"
                value={formik.values.business_phone_number}
                onChange={formik.handleChange}
                error={formik.touched.business_phone_number && Boolean(formik.errors.business_phone_number)}
                helperText={formik.touched.business_phone_number && formik.errors.business_phone_number}
                placeholder="Ex: 07123456 ou +22507123456"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>

          {/* Guide d'aide */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon color="primary" />
                  Guide WhatsApp Business
                </Typography>
                <List dense>
                  <ListItem disablePadding>
                    <ListItemIcon>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Numéro vérifié WhatsApp Business"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemIcon>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Compte Meta Business configuré"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemIcon>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="API WhatsApp Business activée"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                </List>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Rendez-vous sur <strong>business.facebook.com</strong> pour configurer votre compte.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              {onBack && (
                <Button onClick={onBack} disabled={isValidating}>
                  Retour
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                disabled={isValidating || !formik.isValid}
                sx={{ minWidth: 120 }}
              >
                {isValidating ? 'Validation...' : 'Continuer'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
